import React from "react";

import { closestCenter, DndContext, DragOverlay } from "@dnd-kit/core";
import {
  rectSortingStrategy,
  SortableContext,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Lock } from "lucide-react";

import AdminVideoCard from "./AdminVideoCard";
import {
  SORTABLE_DROP_ANIMATION,
  SORTABLE_TRANSITION,
  useSortableListState,
  useSortableSensors,
} from "./sortableDnd";
import { recalculateVideoOrders } from "./videoOrderUtils";
import { Video } from "../../Store/Reducers/VideoSlice";

const getVideoId = (video: Video) => video.video_id;

interface SortableVideoGridProps {
  videos: Video[];
  onVideosChange: (videos: Video[]) => void;
  onPreview: (video: Video) => void;
  onDelete: (video: Video) => void;
  deleteTitle?: string;
  onStatusToggle: (video: Video, toActive: boolean) => void;
  highlightId?: string | null;
  onMoved?: (id: string) => void;
  gridClassName?: string;
}

type CardHandlers = Pick<
  SortableVideoGridProps,
  "onPreview" | "onDelete" | "deleteTitle" | "onStatusToggle"
>;

const noop = () => undefined;

const SortableVideoItem: React.FC<
  CardHandlers & { video: Video; displayVideo: Video; highlighted: boolean }
> = ({ video, displayVideo, highlighted, ...handlers }) => {
  const isPublished = video.is_active ?? true;
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: video.video_id,
    disabled: !isPublished,
    transition: SORTABLE_TRANSITION,
  });

  // Activator = wrapper, so Enter/Space on the card's own buttons never starts a keyboard drag.
  const setRefs = (node: HTMLDivElement | null) => {
    setNodeRef(node);
    setActivatorNodeRef(node);
  };

  return (
    <div
      ref={setRefs}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`sortable-video${isDragging ? " is-placeholder is-drag-active" : ""}${
        highlighted ? " is-reorder-highlight" : ""
      }${isPublished ? "" : " is-locked"}`}
      {...attributes}
      {...listeners}
      aria-label={
        isPublished
          ? `${video.title || "Video"} — drag to reorder`
          : `${video.title || "Video"} — unpublished, not in play order`
      }
    >
      {isPublished ? (
        <span className="sortable-video__handle" aria-hidden="true">
          <GripVertical size={14} />
          Drag
        </span>
      ) : (
        <span className="sortable-video__handle sortable-video__handle--locked">
          <Lock size={12} />
          Unpublished
        </span>
      )}
      <AdminVideoCard
        video={displayVideo}
        isOrderEditMode={false}
        showActionsMenu={false}
        {...handlers}
      />
    </div>
  );
};

/**
 * Edit Order grid: neighbours glide aside while a lifted copy follows the
 * pointer, and order badges renumber live to preview the result.
 */
const SortableVideoGrid: React.FC<SortableVideoGridProps> = ({
  videos,
  onVideosChange,
  highlightId = null,
  onMoved,
  gridClassName = "admin-video-container__grid",
  ...handlers
}) => {
  const sensors = useSortableSensors();
  const {
    activeId,
    ids,
    reset,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    previewOrders: buildPreviewOrders,
  } = useSortableListState({
    items: videos,
    getId: getVideoId,
    onItemsChange: onVideosChange,
    onMoved,
    transformMoved: recalculateVideoOrders,
  });

  const previewOrders = buildPreviewOrders(
    video => video.video_order ?? 0,
    recalculateVideoOrders
  );

  const withPreviewOrder = (video: Video): Video => ({
    ...video,
    video_order: previewOrders.get(video.video_id) ?? video.video_order,
  });

  const activeVideo = activeId
    ? videos.find(video => video.video_id === activeId)
    : undefined;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={reset}
    >
      <SortableContext items={ids} strategy={rectSortingStrategy}>
        <div
          className={`${gridClassName} sortable-video-grid${
            activeId ? " is-sorting" : ""
          }`}
        >
          {videos.map(video => (
            <SortableVideoItem
              key={video.video_id}
              video={video}
              displayVideo={withPreviewOrder(video)}
              highlighted={highlightId === video.video_id}
              {...handlers}
            />
          ))}
        </div>
      </SortableContext>
      <DragOverlay dropAnimation={SORTABLE_DROP_ANIMATION}>
        {activeVideo ? (
          <div className="sortable-video sortable-video--overlay">
            <span className="sortable-video__handle" aria-hidden="true">
              <GripVertical size={14} />
              Drag
            </span>
            <AdminVideoCard
              video={withPreviewOrder(activeVideo)}
              isOrderEditMode={false}
              showActionsMenu={false}
              onPreview={noop}
              onDelete={noop}
              onStatusToggle={noop}
            />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default SortableVideoGrid;
